import { useEffect, useRef } from "react";

import { useArchiveConversation } from "~/ai-chat/data-source/use-conversations";
import { useAiChat } from "~/ai-chat/feature/use-ai-chat";

/**
 * Archive or unarchive a persisted conversation. A successful archive also closes the
 * conversation's tab, and when it is the chat on screen, starts a new chat in its place.
 * A failed archive keeps the tab (the mutation toasts the failure).
 */
export function useArchiveChat() {
  const { activeConversationId, startNewConversation, setTabOpenIds } = useAiChat();
  const { mutateAsync } = useArchiveConversation();

  // The archive resolves after a round trip, by which time the user may have switched
  // chats; the success handler must act on the chat on screen then, not at call time.
  const latestRef = useRef({ activeConversationId, startNewConversation });
  useEffect(() => {
    latestRef.current = { activeConversationId, startNewConversation };
  });

  // mutateAsync rather than per-call mutate callbacks: those are dropped if the history
  // list unmounts mid-request, which would leave the archived chat's tab open. The
  // mutation's own onError already toasts a failure, so the rejection needs no handling here.
  const archive = (conversationId: string, archived: boolean) => {
    mutateAsync({ conversationId, archived })
      .then(() => {
        if (!archived) return;
        setTabOpenIds((prev) => prev.filter((id) => id !== conversationId));
        const latest = latestRef.current;
        if (latest.activeConversationId === conversationId) latest.startNewConversation();
      })
      .catch(() => {});
  };

  return { archive };
}
